import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { CLINIC } from "../lib/data";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/team", label: "Meet the Team" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !open;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        transparent ? "bg-transparent" : "glass-nav"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center group" data-testid="nav-logo">
          <img
            src="/logo.png"
            alt="Arena Movement Physiotherapy logo"
            className="h-10 sm:h-12 w-auto object-contain"
          />
        </Link>

        <div className="hidden lg:flex items-center gap-7 ml-auto">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium whitespace-nowrap transition-colors hover:opacity-100 ${
                  transparent ? "text-white/90 hover:text-white" : isActive ? "text-navy" : "text-text-muted hover:text-navy"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-4 ml-9 pl-9 border-l border-slate-200/70">
          <a
            href={`tel:${CLINIC.phoneIntl}`}
            data-testid="nav-phone"
            className={`flex items-center gap-2 text-sm font-medium whitespace-nowrap ${transparent ? "text-white" : "text-navy"}`}
          >
            <Phone className="h-4 w-4" /> {CLINIC.phone}
          </a>
          <Link
            to="/book"
            data-testid="nav-book-button"
            className="inline-flex items-center rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-navy-hover hover:-translate-y-0.5"
          >
            Book Appointment
          </Link>
        </div>

        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          className={`lg:hidden p-2 rounded-lg ${transparent ? "text-white" : "text-navy"}`}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden glass-nav border-t border-border-light px-5 pb-6 pt-2" data-testid="mobile-menu">
          <div className="flex flex-col">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 text-base font-medium border-b border-border-light ${isActive ? "text-navy" : "text-text-dark"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/book"
              data-testid="mobile-book-button"
              className="mt-5 inline-flex justify-center items-center rounded-full bg-navy px-5 py-3.5 text-base font-semibold text-white"
            >
              Book Appointment
            </Link>
            <a href={`tel:${CLINIC.phoneIntl}`} className="mt-3 inline-flex justify-center items-center gap-2 text-navy font-medium">
              <Phone className="h-4 w-4" /> {CLINIC.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
